import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Space,
  message,
  Divider,
  Typography,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import ButtonGX from '../../webChat/ButtonGX';
import AspApi from '../../../../../api/ApiConfig';
import { useSelector } from 'react-redux';
import { uploadFile as uploadConfigFile } from '../../../../../hooks/useFileUploader';

const { Title, Text } = Typography;

const AnchorFiles = () => {
  const [form] = Form.useForm();
  const [customFields, setCustomFields] = useState([]);
  const BASE_PATH = '/api/v1/svc/ai_agents/anchor_documents';
  const ANCHOR_DOCUMENT_ID = 1;

  const { currentBrand } = useSelector((state) => state.app);

  const addCustomField = () => {
    if (customFields.length < 10) {
      setCustomFields([...customFields, { key: '', value: '' }]);
    }
  };

  const removeCustomField = (index) => {
    const newCustomFields = [...customFields];
    newCustomFields.splice(index, 1);
    setCustomFields(newCustomFields);
    form.setFieldsValue({
      customFields: newCustomFields.map((field) => ({
        key: field.key,
        value: field.value,
      })),
    });
  };

  const handleFieldChange = (index, field, value) => {
    const newCustomFields = [...customFields];
    newCustomFields[index][field] = value;
    setCustomFields(newCustomFields);
  };

  useEffect(() => {
    const fetchAnchorDocument = async () => {
      try {
        const response = await AspApi.get(
          `${BASE_PATH}/${ANCHOR_DOCUMENT_ID}`,
          {
            params: {
              brand_id: currentBrand.brand_id,
            },
          },
        );

        const data = response.data || {};
        const config = data.configuration || {};

        const customFieldsArray = config.custom_fields
          ? Object.entries(config.custom_fields).map(([key, value]) => ({
              key,
              value,
            }))
          : [];

        setCustomFields(customFieldsArray);

        form.setFieldsValue({
          business_name: config.business_name || '',
          business_description: config.business_description || '',
          address: config.address || '',
          phone_number: config.phone_number || '',
          email: config.email || '',
          website: config.website || '',
          customFields: customFieldsArray,
        });
      } catch (error) {
        console.error('Error fetching anchor document:', error);
        message.error('Failed to load anchor files configuration.');
      }
    };

    if (currentBrand?.brand_id) {
      fetchAnchorDocument();
    }
  }, [currentBrand?.brand_id, form]);

  const onFinish = async (values) => {
    console.log('Received values of form: ', values);

    const structuredCustomFields = {};
    if (values.customFields) {
      values.customFields.forEach((field) => {
        if (field.key) {
          structuredCustomFields[field.key] = field.value;
        }
      });
    }

    const configuration = {
      business_name: values.business_name,
      business_description: values.business_description,
      address: values.address,
      phone_number: values.phone_number,
      email: values.email,
      website: values.website,
      custom_fields: structuredCustomFields,
    };

    try {
      const fileContent = JSON.stringify(configuration, null, 2);
      const configTextFile = new File(
        [fileContent],
        'anchor_configuration.txt',
        { type: 'text/plain' },
      );

      const uploadedFileUrl = await uploadConfigFile(configTextFile, {
        context: 'anchor_configuration',
        brand_id: currentBrand.brand_id,
      });

      console.log('Configuration to send: ', configuration);
      console.log('Uploaded configuration file URL: ', uploadedFileUrl);

      const response = await AspApi.put(`${BASE_PATH}/${ANCHOR_DOCUMENT_ID}`, {
        brand_id: currentBrand.brand_id,
        configuration,
        file: uploadedFileUrl,
      });

      if (response.status >= 200 && response.status < 300) {
        console.log('Save successful:', response.data);
        message.success('Anchor files configuration saved successfully!');
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error saving anchor files:', error);
      message.error(
        'Failed to save anchor files configuration. Please try again.',
      );
    }
  };

  return (
    <Card
      title={
        <Title style={{ margin: 0 }} level={4}>
          Anchor File Configuration
        </Title>
      }
      extra={
        <ButtonGX htmlType="submit" onClick={() => form.submit()}>
          Save
        </ButtonGX>
      }
    >
      <Form
        form={form}
        name="anchorFilesForm"
        onFinish={onFinish}
        layout="vertical"
        initialValues={{
          business_name: '',
          business_description: '',
          address: '',
          phone_number: '',
          email: '',
          website: '',
          customFields: customFields,
        }}
        className="flex flex-col gap-3"
      >
        {/* Default Fields */}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={
                <Text style={{ margin: 0 }} className="text-sm font-medium">
                  Business Name
                </Text>
              }
              name="business_name"
              rules={[
                { required: true, message: 'Please input your business name!' },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={
                <Text style={{ margin: 0 }} className="text-sm font-medium">
                  Business Description
                </Text>
              }
              name="business_description"
              rules={[
                {
                  required: true,
                  message: 'Please input your business description!',
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={
                <Text style={{ margin: 0 }} className="text-sm font-medium">
                  Address
                </Text>
              }
              name="address"
              rules={[
                { required: true, message: 'Please input your address!' },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={
                <Text style={{ margin: 0 }} className="text-sm font-medium">
                  Phone Number
                </Text>
              }
              name="phone_number"
              rules={[
                { required: true, message: 'Please input your phone number!' },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={
                <Text style={{ margin: 0 }} className="text-sm font-medium">
                  Email
                </Text>
              }
              name="email"
              rules={[
                { required: true, message: 'Please input your email!' },
                { type: 'email', message: 'The input is not valid E-mail!' },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={
                <Text style={{ margin: 0 }} className="text-sm font-medium">
                  Website
                </Text>
              }
              name="website"
              rules={[
                { required: true, message: 'Please input your website!' },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {/* Custom Fields Section */}
        <Divider orientation="left">Custom Fields</Divider>
        <div className="custom-fields-section flex flex-col gap-3">
          {customFields.map((field, index) => (
            <Row gutter={16} key={index} align="middle">
              <Col span={11}>
                <Form.Item
                  label={`Key Name`}
                  name={['customFields', index, 'key']}
                  rules={[
                    { required: true, message: 'Key is required' },
                    { max: 300, message: 'Key cannot exceed 300 characters' },
                  ]}
                  initialValue={field.key}
                >
                  <Input
                    onChange={(e) =>
                      handleFieldChange(index, 'key', e.target.value)
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={11}>
                <Form.Item
                  label={`Value`}
                  name={['customFields', index, 'value']}
                  rules={[
                    { required: true, message: 'Value is required' },
                    { max: 300, message: 'Value cannot exceed 300 characters' },
                  ]}
                  initialValue={field.value}
                >
                  <Input
                    onChange={(e) =>
                      handleFieldChange(index, 'value', e.target.value)
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={2} className="self-end">
                <Button
                  // danger
                  onClick={() => removeCustomField(index)}
                  icon={<DeleteOutlined />}
                />
              </Col>
            </Row>
          ))}
        </div>

        <ButtonGX
          // type="dashed"
          onClick={addCustomField}
          icon={<PlusOutlined />}
          disabled={customFields.length >= 10}
          className="my-5"
        >
          Add New Field
        </ButtonGX>
      </Form>
    </Card>
  );
};

export default AnchorFiles;
